import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { normalizeJoinCode } from '../lib/joinCode';

/**
 * The public front door: what Project-Workflow is, and the two ways in — create
 * an account and start a team, or redeem a code for a team that already exists.
 * Nothing here talks to the API, so it renders for signed-out visitors offline.
 */
export function Home() {
  return (
    <div className="min-h-dvh">
      <TopBar />
      <main>
        <Hero />
        <Steps />
        <Features />
        <Closing />
      </main>
      <Footer />
    </div>
  );
}

function TopBar() {
  return (
    <header className="sticky top-0 z-20 border-b border-line bg-canvas/85 backdrop-blur-md">
      <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo />
          <span className="text-sm font-semibold tracking-tight">Project-Workflow</span>
        </Link>
        <nav className="ml-auto flex items-center gap-2">
          <Link to="/login" className="btn btn-ghost">
            Sign in
          </Link>
          <Link to="/signup" className="btn btn-primary">
            Create account
          </Link>
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="mx-auto max-w-6xl px-4 pt-16 pb-12 sm:pt-24">
      <div className="animate-rise mx-auto max-w-2xl text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3 py-1 text-xs text-ink-soft">
          <span className="size-1.5 rounded-full bg-done" />
          Works offline, syncs when you're back
        </span>

        <h1 className="mt-5 text-3xl font-semibold tracking-tight text-balance sm:text-5xl">
          Task management that ends in a release, not a backlog
        </h1>

        <p className="mt-4 text-base leading-relaxed text-ink-soft sm:text-lg">
          Your team's work is split into dev fields, tracked on one board, and bundled into versions.
          Release a version and everyone gets the changelog — written for you, posted to Discord.
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to="/signup" className="btn btn-primary !px-5 !py-2.5">
            Create your workspace
          </Link>
          <Link to="/login" className="btn btn-ghost !px-5 !py-2.5">
            Sign in
          </Link>
        </div>

        <p className="mt-3 text-xs text-ink-faint">
          Free to start · no credit card · your team, your data
        </p>
      </div>

      <BoardPreview />
    </section>
  );
}

/** A still of the board, so the first screen shows the product rather than describing it. */
function BoardPreview() {
  const columns = [
    {
      status: 'To do',
      color: 'var(--color-todo)',
      tasks: [
        { title: 'Rework the /warp cooldown', field: 'Plugins', priority: 'high' },
        { title: 'Cache the leaderboard query', field: 'Web', priority: 'medium' },
      ],
    },
    {
      status: 'In progress',
      color: 'var(--color-progress)',
      tasks: [
        { title: 'Fix combat knockback desync', field: 'Plugins', priority: 'critical' },
        { title: 'Move backups to the new volume', field: 'Infrastructure', priority: 'medium' },
      ],
    },
    {
      status: 'Done',
      color: 'var(--color-done)',
      tasks: [
        { title: 'Rebuild the store checkout', field: 'Web', priority: 'high' },
        { title: 'Bump the proxy to 1.21', field: 'Infrastructure', priority: 'low' },
      ],
    },
  ];

  return (
    <div
      className="animate-rise card mx-auto mt-14 max-w-4xl overflow-hidden p-0"
      aria-hidden="true"
    >
      <div className="flex items-center gap-2 border-b border-line bg-elevated/60 px-4 py-2.5">
        <span className="text-xs font-medium">Boom Network</span>
        <span className="text-xs text-ink-faint">· Board</span>
        <span className="ml-auto flex items-center gap-1.5 text-[11px] text-ink-faint">
          <span className="size-1.5 rounded-full bg-done" />
          Synced
        </span>
      </div>

      <div className="grid gap-3 p-4 sm:grid-cols-3">
        {columns.map((column) => (
          <div key={column.status} className="flex flex-col gap-2">
            <div className="flex items-center gap-2 px-0.5">
              <span className="size-1.5 rounded-full" style={{ backgroundColor: column.color }} />
              <span className="text-[11px] font-medium text-ink-soft">{column.status}</span>
              <span className="text-[11px] text-ink-faint">{column.tasks.length}</span>
            </div>
            {column.tasks.map((task) => (
              <div key={task.title} className="rounded-lg border border-line bg-canvas p-2.5">
                <p className="text-xs leading-snug">{task.title}</p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="rounded-full bg-elevated px-1.5 py-0.5 text-[10px] text-ink-faint">
                    {task.field}
                  </span>
                  <span className="text-[10px] text-ink-faint capitalize">{task.priority}</span>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Steps() {
  const steps = [
    {
      title: 'Create an account',
      body: 'Email, a password, and the name your teammates will see. That is the whole signup.',
    },
    {
      title: 'Start a team or join one',
      body: 'Create a team and you own it. Got a join code or an invite link from someone? Redeem it and you are on their board.',
    },
    {
      title: 'Work, then release',
      body: 'Track tasks per dev field. Bundle the finished ones into a version, release it, and the changelog writes itself.',
    },
  ];

  return (
    <section className="border-t border-line bg-surface/40">
      <div className="mx-auto max-w-6xl px-4 py-16">
        <h2 className="text-center text-xl font-semibold tracking-tight sm:text-2xl">
          Three steps to a working board
        </h2>

        <ol className="mt-10 grid gap-4 sm:grid-cols-3">
          {steps.map((step, index) => (
            <li key={step.title} className="card p-5">
              <span className="grid size-7 place-items-center rounded-full bg-accent/15 text-xs font-semibold text-accent">
                {index + 1}
              </span>
              <h3 className="mt-3.5 text-sm font-semibold">{step.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{step.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Features() {
  const features = [
    {
      title: 'Dev fields, not one flat list',
      body: 'Plugins, Web, Infrastructure — whatever your team is split into. Members see the fields they work in; owners and admins see everything.',
    },
    {
      title: 'Versions that close the loop',
      body: 'Gather completed tasks into a release packet, grouped by field and credited to whoever finished each one.',
    },
    {
      title: 'Changelogs you did not write',
      body: 'Releasing a version generates the changelog from the tasks it contains and posts it to your Discord channels.',
    },
    {
      title: 'Offline by default',
      body: 'The board runs from a local cache. Changes you make without a connection queue up and sync in order once you have one.',
    },
    {
      title: 'Roles that mean something',
      body: 'Owners, admins and members. Released versions become read-only history that nobody can quietly rewrite.',
    },
    {
      title: 'Two ways in',
      body: 'Email an invite bound to one address, or hand out a join code that anyone on your team can redeem. Rotate or switch it off whenever.',
    },
  ];

  return (
    <section className="mx-auto max-w-6xl px-4 py-16">
      <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">
        Built for teams that ship in versions
      </h2>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink-soft">
        Every team gets its own workspace: separate boards, members, fields and release history.
      </p>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {features.map((feature) => (
          <div key={feature.title} className="card p-5">
            <h3 className="text-sm font-semibold">{feature.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{feature.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Closing call to action, plus the shortcut for people arriving with a code. */
function Closing() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const normalized = normalizeJoinCode(code);

  function redeem(event: FormEvent) {
    event.preventDefault();
    if (normalized.length === 8) navigate(`/join/${normalized}`);
  }

  return (
    <section className="border-t border-line bg-surface/40">
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">Start your board</h2>
        <p className="mt-2 text-sm leading-relaxed text-ink-soft">
          Create an account, then create a team or join one.
        </p>

        <Link to="/signup" className="btn btn-primary mt-6 !px-5 !py-2.5">
          Create account
        </Link>

        <div className="mt-10 border-t border-line pt-8">
          <p className="text-sm text-ink-soft">Someone gave you a join code?</p>
          <form onSubmit={redeem} className="mx-auto mt-3 flex max-w-sm gap-2">
            <input
              className="field-input font-mono tracking-widest uppercase"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="ABCD-2345"
              maxLength={20}
              aria-label="Join code"
            />
            <button type="submit" className="btn btn-ghost" disabled={normalized.length !== 8}>
              Join
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-line">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-8 text-xs text-ink-faint">
        <span>Project-Workflow</span>
        <Link to="/login" className="ml-auto transition-colors hover:text-ink-soft">
          Sign in
        </Link>
        <Link to="/signup" className="transition-colors hover:text-ink-soft">
          Create account
        </Link>
      </div>
    </footer>
  );
}
